/**
 * Reactions Hooks
 * TanStack Query hooks for reaction operations
 */

import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import * as reactionsService from '@/services/reactions.service';
import { findConversationInInfiniteCache } from './useMessages';
import { useAuthStore } from '@/store/auth.store';
import type { MessageReaction } from '@/types/api.types';
import type { PaginatedMessagesResponse } from '@/services/messages.service';

type InfiniteMessagesData = InfiniteData<PaginatedMessagesResponse>;

/**
 * Apply a reaction transform to a single message inside the paginated
 * (infinite) messages cache. Messages are stored as
 * `['messages','infinite', conversationKey] -> { pages: [{ messages }] }`.
 */
const updateReactionsInCache = (
  data: InfiniteMessagesData | undefined,
  messageId: string,
  updater: (reactions: MessageReaction[]) => MessageReaction[]
): InfiniteMessagesData | undefined => {
  if (!data?.pages) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.map((msg) =>
        msg.id === messageId
          ? { ...msg, message_reactions: updater(msg.message_reactions || []) }
          : msg
      ),
    })),
  };
};

/**
 * Mutation hook to toggle a reaction (add or remove).
 * Optimistically updates the infinite messages cache and reconciles with the
 * server response.
 */
export const useToggleReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      reactionsService.toggleReaction(messageId, emoji),
    onMutate: async ({ messageId, emoji }) => {
      const found = findConversationInInfiniteCache(queryClient, messageId);
      if (!found) return { previousData: undefined };

      const queryKey = ['messages', 'infinite', found.conversationKey];

      // Cancel outgoing refetches so they don't clobber the optimistic update
      await queryClient.cancelQueries({ queryKey });

      const userId = useAuthStore.getState().user?.id || '';

      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) =>
        updateReactionsInCache(old, messageId, (reactions) => {
          const existing = reactions.find(
            (r) => r.emoji === emoji && r.user_id === userId
          );
          if (existing) {
            return reactions.filter((r) => r.id !== existing.id);
          }
          const optimistic: MessageReaction = {
            id: `temp-${Date.now()}`,
            message_id: messageId,
            user_id: userId,
            emoji,
            created_at: new Date().toISOString(),
          };
          return [...reactions, optimistic];
        })
      );

      return { previousData: found.previousData, conversationKey: found.conversationKey };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData && context?.conversationKey) {
        queryClient.setQueryData(
          ['messages', 'infinite', context.conversationKey],
          context.previousData
        );
      }
    },
    onSuccess: (result, variables, context) => {
      if (!context?.conversationKey) return;
      const queryKey = ['messages', 'infinite', context.conversationKey];

      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) =>
        updateReactionsInCache(old, variables.messageId, (reactions) => {
          if (result.action === 'added' && result.reaction) {
            // Replace the optimistic temp reaction with the persisted one
            const withoutTemp = reactions.filter((r) => !r.id.startsWith('temp-'));
            if (withoutTemp.some((r) => r.id === result.reaction!.id)) {
              return withoutTemp;
            }
            return [...withoutTemp, result.reaction!];
          }
          // 'removed' — already applied optimistically
          return reactions;
        })
      );
    },
  });
};

/**
 * Mutation hook to add a reaction
 */
export const useAddReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      reactionsService.addReaction(messageId, emoji),
    onSuccess: () => {
      // Invalidate messages queries to refetch with new reaction
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
};

/**
 * Mutation hook to remove a reaction
 */
export const useRemoveReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      reactionsService.removeReaction(messageId, emoji),
    onSuccess: () => {
      // Invalidate messages queries to refetch without reaction
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
};
